import React, { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import subscriptionService from '../../services/subscriptionService';

const SubscriptionPlansSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.2 });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch plans from backend
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await subscriptionService.getPlans();
        if (response.success && response.plans) {
          setPlans(response.plans);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        // Fallback to default plans if API fails
        setPlans([
          {
            id: 1,
            name: 'Basic',
            plan_type: 'basic',
            price: 9.99,
            checks: 100,
            popular: false,
            features: [
              'Up to 100 IMEI checks per month',
              'Basic device information',
              'Carrier information',
              'Email support',
              'Standard processing speed'
            ]
          },
          {
            id: 2,
            name: 'Professional',
            plan_type: 'professional',
            price: 49.99,
            checks: 1000,
            popular: true,
            features: [
              'Up to 1,000 IMEI checks per month',
              'Advanced device information',
              'Carrier unlock status',
              'Blacklist database access',
              'Priority customer support',
              'API access',
              'Export reports',
              'Fast processing speed'
            ]
          },
          {
            id: 3,
            name: 'Enterprise',
            plan_type: 'enterprise',
            price: 199.99,
            checks: 10000,
            popular: false,
            features: [
              'Up to 10,000 IMEI checks per month',
              'Complete device analytics',
              'Real-time carrier updates',
              'Global blacklist access',
              'Dedicated account manager',
              'Full API access',
              'Custom integrations',
              'White-label solutions',
              'Instant processing speed',
              'Custom reporting'
            ]
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  // Get plan styling based on plan type
  const getPlanStyling = (planType) => {
    switch (planType) {
      case 'basic':
        return {
          color: 'from-gray-500 to-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200'
        };
      case 'professional':
        return {
          color: 'from-primary-600 to-blue-600',
          bgColor: 'bg-primary-50',
          borderColor: 'border-primary-200'
        };
      case 'enterprise':
        return {
          color: 'from-purple-600 to-indigo-600',
          bgColor: 'bg-purple-50',
          borderColor: 'border-purple-200'
        };
      default:
        return {
          color: 'from-gray-500 to-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200'
        };
    }
  };



  return (
    <section id="pricing" ref={ref} className="w-full py-16 lg:py-24 bg-gray-50">
      <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div className="text-center mb-16" variants={itemVariants}>
            <motion.h2 
              className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6"
              variants={itemVariants}
            >
              Choose Your Perfect Plan
            </motion.h2>
            <motion.p 
              className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
              variants={itemVariants}
            >
              Select the subscription plan that best fits your business needs. 
              All plans include our comprehensive IMEI verification services with different usage limits.
            </motion.p>
          </motion.div>

          {/* Loading State */}
          {loading && (
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-12"
              variants={containerVariants}
            >
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-gray-200 animate-pulse">
                  <div className="bg-gray-100 px-6 py-6">
                    <div className="h-6 bg-gray-200 rounded mb-4"></div>
                    <div className="h-12 bg-gray-200 rounded mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                  </div>
                  <div className="px-6 py-6">
                    <div className="space-y-3">
                      {[1, 2, 3, 4].map((j) => (
                        <div key={j} className="h-4 bg-gray-200 rounded"></div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {/* Pricing Cards */}
          {!loading && (
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-12"
              variants={containerVariants}
            >
              {plans.map((plan, index) => {
                const styling = getPlanStyling(plan.plan_type);
                return (
                  <motion.div
                    key={plan.id}
                    className={`relative bg-white rounded-3xl shadow-xl overflow-hidden border-2 transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                      plan.popular 
                        ? 'border-primary-500 ring-4 ring-primary-100' 
                        : 'border-gray-200 hover:border-primary-300'
                    }`}
                    variants={itemVariants}
                    whileHover={{ y: -10 }}
                  >
                    {/* Plan Header */}
                    <div className={`${styling.bgColor} px-6 py-6 text-center relative overflow-hidden`}>
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-4 right-4 w-16 h-16 border-2 border-current rounded-full"></div>
                        <div className="absolute bottom-4 left-4 w-12 h-12 border-2 border-current rounded-lg rotate-45"></div>
                      </div>
                      <div className="relative z-10">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                        <div className="mb-4">
                          <span className="text-5xl font-bold text-gray-900">${plan.price}</span>
                          <span className="text-gray-600 text-lg">/month</span>
                        </div>
                        <p className="text-gray-700 font-medium">
                          Up to {(plan.checks || plan.imei_checks_limit || 0).toLocaleString()} IMEI checks
                        </p>
                      </div>
                    </div>

                    {/* Plan Features */}
                    <div className="px-6 py-6">
                      <ul className="space-y-3">
                        {(plan.features || []).map((feature, featureIndex) => (
                          <motion.li 
                            key={featureIndex} 
                            className="text-left"
                            initial={{ opacity: 0, x: -20 }}
                            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                            transition={{ duration: 0.4, delay: 0.1 * featureIndex }}
                          >
                            <span className="text-gray-700 leading-relaxed">• {feature}</span>
                          </motion.li>
                        ))}
                      </ul>

                      {/* Most Popular Badge */}
                      {plan.popular && (
                        <div className="mt-6 text-center">
                          <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-600 to-blue-600 text-white rounded-full text-sm font-bold shadow-lg">
                            🔥 Most Popular Choice
                          </span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}


        </motion.div>
      </div>
    </section>
  );
};

export default SubscriptionPlansSection;